import type { ListFilterComparator } from "./filters";

/**
 * In-memory evaluation of an "is any of" / "is none of" row over a string
 * field, for lists fetched in one shot and filtered in the browser rather than
 * through `filterToCondition`.
 *
 * `haystacks` are the field's values: one for a scalar field, several for a
 * list field such as a group's members. The field is unset when none of them
 * is non-blank. Values compare case-insensitively, and blank values in the
 * filter are ignored.
 *
 * - `ANY_OF`: some haystack equals some value, or the field is unset and
 *   "Not set" (`includeNull`) is a member.
 * - `NONE_OF`: exactly not `ANY_OF` over the same members. An unset field is
 *   kept unless "Not set" is a member, and "Not set" alone keeps every set
 *   field. Every record matches exactly one of the two.
 *
 * A list with no value and no "Not set" can't be read, so it matches every
 * record under either comparator rather than drop rows over it. The schema
 * rejects such a row; this guards one that gets past it.
 */
export function matchesStringListFilter(
  haystacks: readonly string[],
  comparator: ListFilterComparator,
  values: readonly string[],
  includeNull = false,
): boolean {
  const needles = values
    .filter((value) => value.trim().length > 0)
    .map((value) => value.toLowerCase());
  if (needles.length === 0 && !includeNull) {
    return true;
  }
  const unset = !haystacks.some((haystack) => haystack.trim().length > 0);
  const anyOf =
    (includeNull && unset) ||
    haystacks.some((haystack) => needles.includes(haystack.toLowerCase()));
  return comparator === "ANY_OF" ? anyOf : !anyOf;
}
